import { Departure } from '../data.ts'
import { Filters, SortKey } from '../context/filters.tsx'
import { applyDiscount, cheapestCabinPrice } from '@/lib/departures.ts'

export const SORT_KEYS = ['start_date', 'price'] as const satisfies SortKey[]

export function filterDeparture (departure: Departure, filters: Filters): boolean {
  if (filters.departure.length > 0 && !filters.departure.includes(departure.port_of_departure.id)) {
    return false
  }
  if (filters.ship.length > 0 && !filters.ship.includes(departure.ship.id)) {
    return false
  }
  if (filters.guidance.length > 0) {
    if (departure.tour.guidance == null) {
      return false
    }
    const hasGuidance = (departure.tour.guidance ?? []).some(guidance => filters.guidance.includes(guidance))
    if (!hasGuidance) {
      return false
    }
  }
  if (filters.availability.length > 0) {
    if (!filters.availability.includes(departure.status)) {
      return false
    }
  }
  if (filters.date.length > 0) {
    return filters.date.includes(departure.start_date.substring(0, 7))
  }

  return true
}

export function sortDepartures (departures: Departure[], sort: SortKey): Departure[] {
  switch (sort) {
    case 'start_date':
      return departures.toSorted((a, b) => {
        return a.start_date.localeCompare(b.start_date)
      })
    case 'price': {
      const priceMap = new Map<string, number>()
      const getBasePrice = (departure: Departure): number => {
        const price = priceMap.get(departure.id)
        if (price == null) {
          const newPrice = applyDiscount(departure, cheapestCabinPrice(departure))
          priceMap.set(departure.id, newPrice)
          return newPrice
        } else {
          return price
        }
      }
      return departures.toSorted((a, b) => {
        return getBasePrice(a) - getBasePrice(b)
      })
    }
  }
}

export function applyDepartureFilters (departures: Departure[], filters: Filters): Departure[] {
  const filtered = departures.filter(departure => filterDeparture(departure, filters))
  return filtered.reduce((clean: Departure[], departure: Departure) => {
    if (departure.status === 'fully_booked') {
      const last = clean.at(-1)
      if (last != null && last.status === 'fully_booked') {
        return clean
      }
    }
    clean.push(departure)
    return clean
  }, [])
}

export function removeFullyBooked (departures: Departure[]): Departure[] {
  return departures.reduce((clean: Departure[], departure: Departure) => {
    if (departure.status === 'fully_booked') {
      const last = clean.at(-1)
      if (last != null && last.status === 'fully_booked') {
        return clean
      }
    }
    clean.push(departure)
    return clean
  }, [])
}

export function applyFilterConfiguration (departures: Departure[], filters: Filters, sort: SortKey): Departure[] {
  return removeFullyBooked(sortDepartures(applyDepartureFilters(departures, filters), sort))
}
